import BusAlertIcon from '@mui/icons-material/BusAlert'
import DepartureBoardIcon from '@mui/icons-material/DepartureBoard'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SensorsIcon from '@mui/icons-material/Sensors'
import { List, ListItem, ListItemButton, ListItemText, Stack, useTheme } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Arrival } from 'src/generated/brezavta/requests'
import type { FormatType } from 'src/lib/util'
import { isRouteSelected } from 'src/lib/util'
import { LineChip } from './LineChip'

const ArrivalsList = ({ arrivals, formatTime, getNextTime, selectedRoutes, timeFormat }: { arrivals: Arrival[], formatTime: (arrival: Arrival, arrivals: Arrival[], index: number, format: FormatType, t: (key: string) => string) => string, getNextTime: (seconds: number) => string, selectedRoutes?: Set<string>, timeFormat: FormatType }) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const theme = useTheme()

    // Filter arrivals based on selected routes
    const filteredArrivals = arrivals.filter(arrival => isRouteSelected(arrival, selectedRoutes))

    const clickHandler = (tripId) => {
        navigate({
            to: `/trip/${tripId}`,
        })
    }

    return (
        <List style={{ padding: 0, width: '100%' }}>
            {filteredArrivals && filteredArrivals.length > 0
                ? (
                        filteredArrivals.map((arrival, index) => (
                            <ListItemButton key={index} onClick={() => clickHandler(arrival.trip_id)}>
                                <ListItemText
                                    primary={(
                                        <div style={{ flex: 1 }}>
                                            <LineChip
                                                text={arrival.route_short_name || arrival.trip_headsign || ''}
                                                textColor={arrival.route_color_text}
                                                color={arrival.route_color_background}
                                                extraStyles={{ marginRight: '.5rem' }}
                                            />
                                            {arrival.alerts.length > 0
                                                ? (
                                                        <BusAlertIcon style={{ color: 'orange', marginRight: '5px', fontSize: '1rem' }} />
                                                    )
                                                : (
                                                        <span style={{ width: '24px' }} />
                                                    )}
                                            {arrival.trip_headsign}
                                        </div>
                                    )}
                                    secondary={(arrival.trip_id.includes('IJPP') ? ` ${arrival.agency_name}` : '')}
                                    style={{ textAlign: 'left' }}
                                />
                                <ListItemText
                                    primary={(
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-end" // Ensures alignment to the right
                                            // vertically center the icon and text
                                            alignItems="center"
                                            sx={{ width: '100%' }} // Full-width container to align text properly
                                        >
                                            {arrival.realtime_status === 'CANCELED'
                                                ? (
                                                        <ErrorOutlineIcon style={{ color: 'red', fontSize: '1rem', marginRight: '5px' }} />
                                                    )
                                                : arrival.realtime_status === 'UPDATED'
                                                    ? (
                                                            <SensorsIcon style={{ fontSize: '1rem', animation: 'blink 2s linear infinite', marginRight: '5px' }} />
                                                        )
                                                    : (
                                                            <DepartureBoardIcon style={{ fontSize: '1rem', marginRight: '5px' }} />
                                                        )}
                                            {formatTime(arrival.arrival_realtime, arrival.departure_realtime, timeFormat, t)}
                                        </Stack>
                                    )}
                                    secondary={getNextTime(arrival, filteredArrivals, index, timeFormat, t)}
                                    sx={{
                                        textAlign: 'right', // Ensures text alignment to the right
                                        color: arrival.realtime_status === 'CANCELED' ? 'red' : arrival.realtime_status === 'UPDATED' ? 'green' : theme.palette.text.primary,
                                        minWidth: '6rem', // Maintain the minimum width for consistent alignment
                                    }}
                                />
                            </ListItemButton>
                        ))
                    )
                : (
                        <ListItem key="none">
                            <ListItemText primary={t('No more arrivals are planned for today.')} key="0" />
                        </ListItem>
                    )}
        </List>
    )
}

export default ArrivalsList
